import { useCallback, useEffect, useState } from 'react';
import { notificationsSupported } from '@/utils/notifications';
import { hasPushSubscription, syncPushSubscription } from '@/utils/pushClient';
import { getReadAloud, getTtsVoice, saveReadAloud, saveTtsVoice } from '@/utils/tts';

const LS = { sounds: 'comms:sounds', muted: 'comms:mutedChannels', prompted: 'comms:alertsPrompted' };

const readMuted = () => {
  try { return JSON.parse(localStorage.getItem(LS.muted) || '[]'); } catch { return []; }
};

// Per-device alert preferences (sounds, muted channels, notification permission prompt, spoken messages).
export function useAlertSettings() {
  const [soundsOn, setSoundsOnState] = useState(() => localStorage.getItem(LS.sounds) !== 'off');
  const [mutedChannels, setMutedChannels] = useState(readMuted);
  const [notifPermission, setNotifPermission] = useState(() => (notificationsSupported() ? Notification.permission : 'unsupported'));
  const [alertsPrompted, setAlertsPrompted] = useState(() => localStorage.getItem(LS.prompted) === '1');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [ttsVoice, setTtsVoiceState] = useState(getTtsVoice);
  const [readAloud, setReadAloudState] = useState(getReadAloud);

  // Re-register this device for background push whenever permission is granted or mutes change.
  useEffect(() => {
    if (notifPermission !== 'granted') { setPushEnabled(false); return; }
    let cancelled = false;
    const sync = () => syncPushSubscription(mutedChannels)
      .then((ok) => { if (!cancelled) setPushEnabled(ok); })
      .catch(async () => { if (!cancelled) setPushEnabled(await hasPushSubscription()); });
    sync();
    const onSwMessage = (e) => { if (e.data?.type === 'push-resubscribe') sync(); };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
    return () => {
      cancelled = true;
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
    };
  }, [notifPermission, mutedChannels]);

  const setSoundsOn = useCallback((on) => {
    localStorage.setItem(LS.sounds, on ? 'on' : 'off');
    setSoundsOnState(on);
  }, []);

  const setTtsVoice = useCallback((voice) => { saveTtsVoice(voice); setTtsVoiceState(voice); }, []);
  const setReadAloud = useCallback((on) => { saveReadAloud(on); setReadAloudState(on); }, []);

  const toggleMute = useCallback((teamId) => {
    setMutedChannels((prev) => {
      const next = prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId];
      localStorage.setItem(LS.muted, JSON.stringify(next));
      return next;
    });
  }, []);

  const dismissAlertsBar = useCallback(() => {
    localStorage.setItem(LS.prompted, '1');
    setAlertsPrompted(true);
  }, []);

  const requestAlerts = useCallback(async () => {
    if (!notificationsSupported()) return 'unsupported';
    let result = 'denied';
    try { result = await Notification.requestPermission(); } catch { /* older callback API */ }
    setNotifPermission(result);
    dismissAlertsBar();
    return result;
  }, [dismissAlertsBar]);

  return {
    soundsOn,
    setSoundsOn,
    ttsVoice,
    setTtsVoice,
    readAloud,
    setReadAloud,
    mutedChannels,
    toggleMute,
    notifPermission,
    pushEnabled,
    requestAlerts,
    dismissAlertsBar,
    alertsBarVisible: notifPermission === 'default' && !alertsPrompted,
    alertsBellVisible: notifPermission === 'default' && alertsPrompted,
  };
}
